/**
 * Bridge payment routes and supported currencies constants
 * Reference: https://apidocs.bridge.xyz/get-started/introduction/what-we-support/payment-routes
 *
 * This file contains the supported currencies for each blockchain/payment rail
 * and utility functions to validate and filter payment options.
 */

import type { CustomerDetails } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';

// cspell:ignore usdp pyusd eurc usdb usdt

/**
 * Supported currencies per blockchain/payment rail
 * Key: payment rail value (matches the values used in crypto wallet forms)
 * Value: array of supported currency codes (lowercase)
 */
export const BRIDGE_SUPPORTED_CURRENCIES: Record<string, string[]> = {
  base: ['usdc', 'usdb'],
  solana: ['usdc', 'pyusd', 'usdb', 'usdp', 'eurc'],
  ethereum: ['dai', 'eth', 'pyusd', 'usdc', 'usdt'],
  arbitrum: ['usdc'],
  avalanche_c_chain: ['usdc'],
  optimism: ['usdc'],
  polygon: ['usdc'],
  stellar: ['usdc'],
  tron: ['usdt'],
};

/**
 * Payment rail options for crypto wallets with their display labels
 */
export const CRYPTO_PAYMENT_RAILS = [
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'avalanche_c_chain', label: 'Avalanche C-Chain' },
  { value: 'base', label: 'Base' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'optimism', label: 'Optimism' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'solana', label: 'Solana' },
  { value: 'stellar', label: 'Stellar' },
  { value: 'tron', label: 'Tron' },
] as const;

/**
 * Validates if a currency is supported on a given blockchain/payment rail
 * @param chain - The blockchain or payment rail (e.g., 'base', 'solana', 'ethereum')
 * @param currency - The currency to validate (e.g., 'usdt', 'usdc', 'usdb')
 * @returns boolean indicating if the currency is supported on the chain
 */
export const isCurrencySupportedOnChain = (chain: string | undefined, currency: string | undefined): boolean => {
  if (!chain || !currency) return false;
  const normalizedChain = chain.toLowerCase();
  const normalizedCurrency = currency.toLowerCase();
  const supportedCurrencies = BRIDGE_SUPPORTED_CURRENCIES[normalizedChain];
  if (!supportedCurrencies) return false;
  return supportedCurrencies.includes(normalizedCurrency);
};

/**
 * Gets the list of supported currencies for a given chain
 * @param chain - The blockchain or payment rail
 * @returns Array of supported currencies or empty array if chain not found
 */
export const getSupportedCurrenciesForChain = (chain: string | undefined): string[] => {
  if (!chain) return [];
  return BRIDGE_SUPPORTED_CURRENCIES[chain.toLowerCase()] || [];
};

/** Type for a single payment rail option */
export type PaymentRailOption = { value: string; label: string };

/**
 * Gets the list of payment rails that support a given currency
 * @param currency - The currency code (e.g., 'usdc', 'usdt')
 * @returns Array of payment rail options that support the currency
 */
export const getPaymentRailsForCurrency = (currency: string | undefined): PaymentRailOption[] => {
  if (!currency) return [...CRYPTO_PAYMENT_RAILS];
  const normalizedCurrency = currency.toLowerCase();
  return CRYPTO_PAYMENT_RAILS.filter((rail) => {
    const supportedCurrencies = BRIDGE_SUPPORTED_CURRENCIES[rail.value];
    return supportedCurrencies?.includes(normalizedCurrency);
  });
};

/**
 * User-facing labels for the user-actionable Bridge endorsement requirement codes.
 * Bridge's `missing` requirements mix items the user can resolve (identity details,
 * documents, questionnaires) with internal review checks they cannot act on — e.g.
 * `blocklist_lookup`, `adverse_media_screen`, `post_processing`, `manual_review`. This map
 * therefore doubles as an allowlist: only codes listed here are surfaced, so internal
 * checks are never shown to the user as something to "complete". Codes are the snake_case
 * identifiers from
 * https://apidocs.bridge.xyz/platform/customers/customers/missing-requirements-and-issues.
 * `fresh_*` variants are Bridge's EU re-verification requests (re-confirm an existing
 * detail), so they intentionally share the label of their base field.
 */
export const BRIDGE_REQUIREMENT_LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  date_of_birth: 'Date of birth',
  address_of_residence: 'Residential address',
  tax_identification_number: 'Tax identification number',
  email_address: 'Email address',
  fresh_first_name: 'First name',
  fresh_last_name: 'Last name',
  fresh_date_of_birth: 'Date of birth',
  fresh_address_of_residence: 'Residential address',
  fresh_tax_identification_number: 'Tax identification number',
  proof_of_address_document: 'Proof of address',
  government_id_verification: 'Government-issued ID verification',
  source_of_funds_questionnaire: 'Source of funds questionnaire',
  minimal_source_of_funds_data: 'Source of funds details',
  terms_of_service_v1: 'Terms of service acceptance',
  terms_of_service_v2: 'Terms of service acceptance',
};

/**
 * Requirement codes resolved by Bridge's Persona-hosted source-of-funds questionnaire,
 * which is a separate flow from the cards KYC link. Everything else in the allowlist is
 * resolved through the cards KYC link.
 */
const BRIDGE_SOURCE_OF_FUNDS_REQUIREMENTS = new Set(['source_of_funds_questionnaire']);

/**
 * Bridge reports this issue on an endorsement the customer can never obtain from their
 * region. Its missing requirements are unfulfillable, so they must not be shown as tasks.
 */
const ENDORSEMENT_UNAVAILABLE_IN_REGION_ISSUE = 'endorsement_not_available_in_customers_region';

export interface MissingBridgeRequirements {
  /** Resolved via the cards KYC (Bridge hosted) link. */
  identity: string[];
  /** Resolved via the Persona source-of-funds questionnaire link. */
  sourceOfFunds: string[];
}

/**
 * Splits the user-actionable outstanding ("missing") requirements across all of the
 * customer's Bridge endorsements (cards, base, sepa, …) into the two groups that resolve
 * through different links: identity items (cards KYC link) and the source-of-funds
 * questionnaire (Persona link). Internal Bridge review checks and unmapped codes are
 * excluded, as are endorsements Bridge flags as unavailable in the customer's region
 * (their requirements can never be completed), and labels are de-duplicated so the same
 * code on multiple endorsements (or a field and its `fresh_*` variant) collapses to one
 * entry. Empty groups mean nothing for the user to do via that route.
 */
export const getMissingBridgeRequirements = (
  bridgeCustomer: CustomerDetails | undefined,
): MissingBridgeRequirements => {
  const codes = (bridgeCustomer?.endorsements ?? [])
    .filter((endorsement) => !endorsement.requirements?.issues?.includes(ENDORSEMENT_UNAVAILABLE_IN_REGION_ISSUE))
    .flatMap((endorsement) => [
      ...(endorsement.requirements?.missing?.allOf ?? []),
      ...(endorsement.requirements?.missing?.anyOf ?? []),
    ]);
  const labelsFor = (isSourceOfFunds: boolean): string[] =>
    Array.from(
      new Set(
        codes
          .filter((code) => BRIDGE_SOURCE_OF_FUNDS_REQUIREMENTS.has(code) === isSourceOfFunds)
          .map((code) => BRIDGE_REQUIREMENT_LABELS[code])
          .filter(Boolean),
      ),
    );
  return { identity: labelsFor(false), sourceOfFunds: labelsFor(true) };
};

// Production Bridge Persona ids; override via env for sandbox/staging. These are not secret
// (they appear in the verification URL the user visits).
// cspell:disable
const DEFAULT_BRIDGE_PERSONA_SOF_TEMPLATE_ID = 'itmpl_siHttkPNqrB1JzL9YZiFQmYY7g3Z';
const DEFAULT_BRIDGE_DEVELOPER_ID = '40c24c29-0a69-491e-9c15-2aee0e5c8a76';
// cspell:enable
const BRIDGE_PERSONA_SOF_TEMPLATE_ID =
  process.env.NEXT_PUBLIC_BRIDGE_PERSONA_SOF_TEMPLATE_ID || DEFAULT_BRIDGE_PERSONA_SOF_TEMPLATE_ID;
const BRIDGE_DEVELOPER_ID = process.env.NEXT_PUBLIC_BRIDGE_DEVELOPER_ID || DEFAULT_BRIDGE_DEVELOPER_ID;

/**
 * Builds the Bridge source-of-funds questionnaire link for a customer. Bridge collects this
 * requirement through a Persona-hosted inquiry (not the Bridge KYC link), keyed on the Bridge
 * customer id via Persona's `reference-id`. Returns null when the customer id is missing.
 */
export const getSourceOfFundsQuestionnaireUrl = (bridgeCustomerId: string | undefined): string | null => {
  if (!bridgeCustomerId || !BRIDGE_PERSONA_SOF_TEMPLATE_ID || !BRIDGE_DEVELOPER_ID) return null;
  const query = [
    `inquiry-template-id=${encodeURIComponent(BRIDGE_PERSONA_SOF_TEMPLATE_ID)}`,
    `fields[developer_id]=${encodeURIComponent(BRIDGE_DEVELOPER_ID)}`,
    `reference-id=${encodeURIComponent(bridgeCustomerId)}`,
  ].join('&');
  return `https://bridge.withpersona.com/verify?${query}`;
};
